import { useEffect, useState, type RefObject } from 'react'

export type CameraStatus = 'starting' | 'live' | 'denied' | 'unavailable'

/** Задняя камера, 1080p: без размера iOS даёт 640×480, и мелкий штрихкод не читается (донор, задача 366). */
const CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: 'environment' },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
}

/**
 * Поток задней камеры в `<video>`: запрашивается при монтировании окна,
 * гасится при размонтировании — иначе индикатор камеры горит после закрытия.
 * `denied` — доступ запрещён (подсказать, где включить); `unavailable` —
 * камеры нет или браузер не даёт поток (HTTP без TLS, старый WebView).
 */
export function useCameraStream(video: RefObject<HTMLVideoElement | null>): CameraStatus {
  const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices
  const [status, setStatus] = useState<CameraStatus>(() =>
    typeof media?.getUserMedia === 'function' ? 'starting' : 'unavailable',
  )

  useEffect(() => {
    if (typeof media?.getUserMedia !== 'function') return undefined
    let cancelled = false
    let stream: MediaStream | null = null
    const element = video.current
    media
      .getUserMedia({ video: CONSTRAINTS, audio: false })
      .then((got) => {
        stream = got
        if (cancelled) {
          got.getTracks().forEach((track) => track.stop())
          return
        }
        if (element !== null) {
          element.srcObject = got
          // play() на iOS требует playsInline и muted — они на элементе.
          void element.play().catch(() => undefined)
        }
        setStatus('live')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const name = error instanceof Error ? error.name : ''
        setStatus(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable')
      })
    return () => {
      cancelled = true
      stream?.getTracks().forEach((track) => track.stop())
      if (element !== null) element.srcObject = null
    }
  }, [media, video])

  return status
}
